import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, EyeOff, ExternalLink, ChevronLeft, ChevronRight, ArrowUpDown, ShieldCheck, ShieldX, User, Clock, Settings, Gift, DollarSign, Presentation } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { useAdminData } from './AdminContext';
import { formatDateTime } from './utils';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminListingsTab = () => {
  const navigate = useNavigate();
  const { listings, toggleListingVisibility, refreshData } = useAdminData();
  
  const [listingsSearch, setListingsSearch] = useState('');
  const [listingsCategoryFilter, setListingsCategoryFilter] = useState('all');
  const [listingsStatusFilter, setListingsStatusFilter] = useState('all');
  const [listingsStateFilter, setListingsStateFilter] = useState('all');
  const [listingsSortBy, setListingsSortBy] = useState('created_at');
  const [listingsSortOrder, setListingsSortOrder] = useState('desc');
  const [listingsPage, setListingsPage] = useState(1);
  const [listingsPerPage] = useState(25);
  
  // Booking Rules Dialog State
  const [bookingRulesOpen, setBookingRulesOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [bookingRules, setBookingRules] = useState({
    allow_same_day: false,
    same_day_cutoff_hour: 15,
    same_day_cutoff_minute: 0,
    allow_next_day: false,
    min_notice_hours: 0
  });
  const [adminNote, setAdminNote] = useState('');
  const [savingRules, setSavingRules] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);

  // Fee Status Dialog State
  const [feeStatusOpen, setFeeStatusOpen] = useState(false);
  const [feeStatus, setFeeStatus] = useState(null);
  const [feeActionLoading, setFeeActionLoading] = useState(false);
  const [waiverDays, setWaiverDays] = useState(90);
  const [extendDays, setExtendDays] = useState(30);
  const [fixingVisibility, setFixingVisibility] = useState(false);
  // Phase 2: confirmation flow for overriding an active waiver
  const [overrideConfirmOpen, setOverrideConfirmOpen] = useState(false);
  const [overrideConflict, setOverrideConflict] = useState(null);

  // Fix visibility issues for all vendors
  const fixVisibilityIssues = async () => {
    if (!window.confirm('This will fix visibility field mismatches for all vendors. This is a one-time operation to fix legacy data. Continue?')) {
      return;
    }
    
    setFixingVisibility(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/api/admin/fix-vendor-visibility`, {}, { headers });
      
      const result = response.data;
      toast.success(`Fixed ${result.fixed_count} vendors out of ${result.total_vendors}`);
      
      // Refresh listings to show updated data
      if (refreshData) {
        refreshData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix visibility issues');
    } finally {
      setFixingVisibility(false);
    }
  };

  const categories = useMemo(() => {
    const cats = [...new Set(listings.map(l => l.category).filter(Boolean))];
    return cats.sort();
  }, [listings]);

  const states = useMemo(() => {
    const stateList = [...new Set(listings.map(l => l.state).filter(Boolean))];
    return stateList.sort();
  }, [listings]);

  // Open fee status dialog for a vendor
  const openFeeStatus = async (vendor) => {
    setSelectedVendor(vendor);
    setFeeStatus(null);
    setWaiverDays(90);
    setExtendDays(30);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/api/admin/vendors/${vendor.id}/fee-status`, { headers });
      setFeeStatus(response.data);
      setFeeStatusOpen(true);
    } catch (error) {
      toast.error('Failed to load fee status');
    }
  };

  // Apply fee waiver — guarded by active-waiver overwrite check (Phase 2)
  const applyFeeWaiver = async (force = false) => {
    if (!selectedVendor) return;

    setFeeActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(`${API}/api/admin/vendors/${selectedVendor.id}/apply-fee-waiver`, {
        duration_days: waiverDays,
        fee_percent: 0,
        reason: `Admin-applied ${waiverDays}-day waiver`,
        force,
      }, { headers });

      toast.success(force
        ? `Existing waiver overridden — new ${waiverDays}-day waiver applied`
        : `Fee waiver applied for ${waiverDays} days`);
      setOverrideConfirmOpen(false);
      setOverrideConflict(null);
      // Refresh fee status
      openFeeStatus(selectedVendor);
    } catch (error) {
      // 409: vendor already has an active waiver — surface confirmation flow
      if (error?.response?.status === 409) {
        const detail = error.response.data?.detail || {};
        setOverrideConflict({
          message: detail.message || 'Vendor already has an active waiver.',
          activeUntil: detail.active_until,
          currentFeePercent: detail.current_fee_percent,
          currentReason: detail.current_reason,
        });
        setOverrideConfirmOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to apply fee waiver');
      }
    } finally {
      setFeeActionLoading(false);
    }
  };

  // Confirm-and-force the override
  const confirmForceOverride = async () => {
    await applyFeeWaiver(true);
  };

  // End fee waiver
  const endFeeWaiver = async () => {
    if (!selectedVendor) return;
    
    setFeeActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/api/admin/vendors/${selectedVendor.id}/end-fee-waiver`, {}, { headers });
      
      toast.success('Fee waiver ended');
      openFeeStatus(selectedVendor);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end fee waiver');
    } finally {
      setFeeActionLoading(false);
    }
  };

  // Extend fee waiver
  const extendFeeWaiver = async () => {
    if (!selectedVendor) return;
    
    setFeeActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/api/admin/vendors/${selectedVendor.id}/extend-fee-waiver`, {
        additional_days: extendDays
      }, { headers });
      
      toast.success(`Fee waiver extended by ${extendDays} days`);
      openFeeStatus(selectedVendor);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend fee waiver');
    } finally {
      setFeeActionLoading(false);
    }
  };

  // Open booking rules dialog for a vendor
  const openBookingRules = async (vendor) => {
    setSelectedVendor(vendor);
    setAdminNote('');
    setAuditHistory([]);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/api/admin/vendors/${vendor.id}/booking-rules`, { headers });
      
      setBookingRules({
        allow_same_day: response.data.booking_rules?.allow_same_day || false,
        same_day_cutoff_hour: response.data.booking_rules?.same_day_cutoff_hour || 15,
        same_day_cutoff_minute: response.data.booking_rules?.same_day_cutoff_minute || 0,
        allow_next_day: response.data.booking_rules?.allow_next_day || false,
        min_notice_hours: response.data.booking_rules?.min_notice_hours || 0
      });
      setAuditHistory(response.data.audit_history || []);
      setBookingRulesOpen(true);
    } catch (error) {
      toast.error('Failed to load booking rules');
    }
  };

  // Save booking rules
  const saveBookingRules = async () => {
    if (!selectedVendor) return;
    if (!adminNote.trim()) {
      toast.error('Please provide a reason for this change');
      return;
    }
    
    setSavingRules(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.patch(`${API}/api/admin/vendors/${selectedVendor.id}/booking-rules`, {
        ...bookingRules,
        admin_note: adminNote
      }, { headers });
      
      toast.success('Booking rules updated successfully');
      setBookingRulesOpen(false);
      if (refreshData) refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking rules');
    } finally {
      setSavingRules(false);
    }
  };

  // Format cutoff time for display
  const formatCutoffTime = (hour, minute) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
  };

  // Get availability badge for a vendor
  const getAvailabilityBadge = (vendor) => {
    if (vendor.allow_same_day) {
      return { text: 'Same-Day', color: 'bg-emerald-100 text-emerald-800' };
    }
    if (vendor.allow_next_day) {
      return { text: 'Next-Day', color: 'bg-blue-100 text-blue-800' };
    }
    const minNoticeDays = Math.floor((vendor.min_notice_hours || 0) / 24);
    if (minNoticeDays > 0) {
      return { text: `${minNoticeDays}d Notice`, color: 'bg-gray-100 text-gray-700' };
    }
    return { text: 'Default', color: 'bg-gray-50 text-gray-500' };
  };

  const filteredListings = useMemo(() => {
    let filtered = listings.filter(l => {
      const matchesSearch = listingsSearch === '' || 
        l.business_name?.toLowerCase().includes(listingsSearch.toLowerCase()) ||
        l.vendor_name?.toLowerCase().includes(listingsSearch.toLowerCase()) ||
        l.user_name?.toLowerCase().includes(listingsSearch.toLowerCase()) ||
        l.user_email?.toLowerCase().includes(listingsSearch.toLowerCase()) ||
        l.city?.toLowerCase().includes(listingsSearch.toLowerCase());
      const matchesCategory = listingsCategoryFilter === 'all' || 
        l.primary_service === listingsCategoryFilter ||
        (l.categories && l.categories.includes(listingsCategoryFilter));
      
      // Enhanced visibility filter — must match the actual Browse gate:
      //   vendor_active + admin_visible + participation_agreement_accepted + profile_complete
      // (User suspension/deletion is not present on row payload; backend gate covers it.)
      const vendorActive = l.vendor_active !== false;  // Default true if not set
      const adminVisible = l.admin_visible !== false;  // Default true if not set
      const agreementAccepted = l.participation_agreement_accepted === true;
      const profileComplete = Boolean(
        (l.business_name || '').trim() &&
        ((l.city || '').trim() || (l.state || '').trim()) &&
        (l.profile_photo || '').trim()
      );
      const isVisibleToCustomers = vendorActive && adminVisible && agreementAccepted && profileComplete;
      
      const matchesStatus = listingsStatusFilter === 'all' || 
        (listingsStatusFilter === 'visible' && isVisibleToCustomers) ||
        (listingsStatusFilter === 'hidden' && !isVisibleToCustomers) ||
        (listingsStatusFilter === 'admin_blocked' && !adminVisible) ||
        (listingsStatusFilter === 'vendor_inactive' && !vendorActive && adminVisible);
      
      const matchesState = listingsStateFilter === 'all' || l.state === listingsStateFilter;
      return matchesSearch && matchesCategory && matchesStatus && matchesState;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      if (listingsSortBy === 'created_at') {
        aVal = new Date(a.created_at);
        bVal = new Date(b.created_at);
      } else if (listingsSortBy === 'business_name') {
        aVal = (a.business_name || '').toLowerCase();
        bVal = (b.business_name || '').toLowerCase();
      }
      if (listingsSortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [listings, listingsSearch, listingsCategoryFilter, listingsStatusFilter, listingsStateFilter, listingsSortBy, listingsSortOrder]);

  const totalListingsPages = Math.ceil(filteredListings.length / listingsPerPage);
  const paginatedListings = filteredListings.slice(
    (listingsPage - 1) * listingsPerPage,
    listingsPage * listingsPerPage
  );

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Listing Management</CardTitle>
            <CardDescription>Control vendor listing visibility</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fixVisibilityIssues}
            disabled={fixingVisibility}
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            data-testid="fix-visibility-btn"
          >
            {fixingVisibility ? (
              <>
                <Settings className="h-4 w-4 mr-2 animate-spin" />
                Fixing...
              </>
            ) : (
              <>
                <Settings className="h-4 w-4 mr-2" />
                Fix Visibility Issues
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by business name, vendor, or city..."
                value={listingsSearch}
                onChange={(e) => { setListingsSearch(e.target.value); setListingsPage(1); }}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={listingsCategoryFilter} onValueChange={(v) => { setListingsCategoryFilter(v); setListingsPage(1); }}>
            <SelectTrigger className="w-full md:w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={listingsStateFilter} onValueChange={(v) => { setListingsStateFilter(v); setListingsPage(1); }}>
            <SelectTrigger className="w-full md:w-32">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {states.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={listingsStatusFilter} onValueChange={(v) => { setListingsStatusFilter(v); setListingsPage(1); }}>
            <SelectTrigger className="w-full md:w-40">
              <SelectValue placeholder="Visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="visible">Visible in Browse</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
              <SelectItem value="admin_blocked">Admin Blocked</SelectItem>
              <SelectItem value="vendor_inactive">Vendor Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Listings Table */}
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    if (listingsSortBy === 'business_name') {
                      setListingsSortOrder(listingsSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setListingsSortBy('business_name');
                      setListingsSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center">
                    Business Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Vendor Active</TableHead>
                <TableHead>Admin Status</TableHead>
                <TableHead>Booking Rules</TableHead>
                <TableHead>Pres.</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    if (listingsSortBy === 'created_at') {
                      setListingsSortOrder(listingsSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setListingsSortBy('created_at');
                      setListingsSortOrder('desc');
                    }
                  }}
                >
                  <div className="flex items-center">
                    Created
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead className="text-right">Admin Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedListings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    No listings found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedListings.map((listing) => {
                  const vendorActive = listing.vendor_active !== false;
                  const adminVisible = listing.admin_visible !== false;
                  const agreementAccepted = listing.participation_agreement_accepted === true;
                  const profileComplete = Boolean(
                    (listing.business_name || '').trim() &&
                    ((listing.city || '').trim() || (listing.state || '').trim()) &&
                    (listing.profile_photo || '').trim()
                  );
                  const isVisibleToCustomers =
                    vendorActive && adminVisible && agreementAccepted && profileComplete;
                  // Compose a short hidden-reason hint for admins (display-only, no behavior change)
                  const hiddenReasons = [];
                  if (!vendorActive) hiddenReasons.push('inactive');
                  if (!adminVisible) hiddenReasons.push('admin-blocked');
                  if (!agreementAccepted) hiddenReasons.push('no agreement');
                  if (!profileComplete) hiddenReasons.push('profile incomplete');
                  
                  return (
                    <TableRow key={listing.id} className={!isVisibleToCustomers ? 'bg-gray-50' : ''}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span>{listing.business_name}</span>
                          {!isVisibleToCustomers && hiddenReasons.length > 0 && (
                            <span
                              data-testid={`hidden-reason-${listing.id}`}
                              className="text-[11px] text-amber-700 mt-0.5"
                            >
                              Hidden: {hiddenReasons.join(', ')}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-gray-900">{listing.vendor_name || listing.user_name || '-'}</span>
                          <span className="text-xs text-gray-500">{listing.user_email || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {listing.primary_service && (
                            <Badge variant="outline" className="w-fit">{listing.primary_service}</Badge>
                          )}
                          {listing.categories && listing.categories.length > 0 && !listing.primary_service && (
                            <Badge variant="outline" className="w-fit">{listing.categories[0]}</Badge>
                          )}
                          {listing.categories && listing.categories.length > 1 && (
                            <span className="text-xs text-gray-400">+{listing.categories.length - 1} more</span>
                          )}
                          {!listing.primary_service && (!listing.categories || listing.categories.length === 0) && (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {listing.city}, {listing.state}
                      </TableCell>
                      {/* Vendor Active Status */}
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge className={vendorActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}>
                                <User className="h-3 w-3 mr-1" />
                                {vendorActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{vendorActive ? 'Vendor has activated their profile' : 'Vendor has deactivated their profile'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      {/* Admin Status */}
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge className={adminVisible ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}>
                                {adminVisible ? (
                                  <><ShieldCheck className="h-3 w-3 mr-1" />Allowed</>
                                ) : (
                                  <><ShieldX className="h-3 w-3 mr-1" />Blocked</>
                                )}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{adminVisible ? 'Admin allows this listing' : 'Admin has blocked this listing'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      {/* Booking Rules */}
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-auto py-1 px-2"
                                onClick={() => openBookingRules(listing)}
                              >
                                <Badge className={getAvailabilityBadge(listing).color}>
                                  <Clock className="h-3 w-3 mr-1" />
                                  {getAvailabilityBadge(listing).text}
                                </Badge>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Click to manage booking rules</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      {/* Presentation Flag */}
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const token = localStorage.getItem('token');
                                    const res = await axios.patch(`${API}/api/admin/listings/${listing.id}/presentation`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    listing.is_presentation_profile = res.data.is_presentation_profile;
                                    toast.success(res.data.is_presentation_profile ? 'Added to presentation' : 'Removed from presentation');
                                    refreshData();
                                  } catch { toast.error('Failed to toggle'); }
                                }}
                                className={`h-7 w-7 rounded-md flex items-center justify-center transition-colors ${listing.is_presentation_profile ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                                data-testid={`pres-toggle-${listing.id}`}
                              >
                                <Presentation className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{listing.is_presentation_profile ? 'Presentation vendor (click to remove)' : 'Click to add to presentation set'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDateTime(listing.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant={adminVisible ? 'destructive' : 'default'}
                            onClick={() => toggleListingVisibility(listing.id, adminVisible)}
                          >
                            {adminVisible ? (
                              <><ShieldX className="h-4 w-4 mr-1" />Block</>
                            ) : (
                              <><ShieldCheck className="h-4 w-4 mr-1" />Unblock</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openFeeStatus(listing)}
                            title="Manage platform fee"
                          >
                            <DollarSign className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/vendors/${listing.id}`)}
                            title="View vendor profile"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalListingsPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-600">
              Showing {((listingsPage - 1) * listingsPerPage) + 1} to {Math.min(listingsPage * listingsPerPage, filteredListings.length)} of {filteredListings.length} listings
            </p>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setListingsPage(p => Math.max(1, p - 1))}
                disabled={listingsPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {listingsPage} of {totalListingsPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setListingsPage(p => Math.min(totalListingsPages, p + 1))}
                disabled={listingsPage === totalListingsPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Booking Rules Dialog */}
    <Dialog open={bookingRulesOpen} onOpenChange={setBookingRulesOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Booking Rules: {selectedVendor?.business_name}
          </DialogTitle>
          <DialogDescription>
            Manage this vendor&apos;s booking lead-time rules. Changes will be logged in the audit history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Same-Day Bookings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">Allow Same-Day Bookings</Label>
                <p className="text-sm text-gray-500">Let customers book services for today</p>
              </div>
              <Switch 
                checked={bookingRules.allow_same_day}
                onCheckedChange={(checked) => setBookingRules(prev => ({ ...prev, allow_same_day: checked }))}
              />
            </div>
            
            {bookingRules.allow_same_day && (
              <div className="ml-4 p-3 bg-gray-50 rounded-lg space-y-2">
                <Label className="text-sm">Same-Day Cutoff Time (Eastern Time)</Label>
                <div className="flex items-center gap-2">
                  <Select 
                    value={String(bookingRules.same_day_cutoff_hour > 12 ? bookingRules.same_day_cutoff_hour - 12 : bookingRules.same_day_cutoff_hour === 0 ? 12 : bookingRules.same_day_cutoff_hour)}
                    onValueChange={(val) => {
                      const hour = parseInt(val);
                      const isPM = bookingRules.same_day_cutoff_hour >= 12;
                      let newHour = hour;
                      if (isPM && hour !== 12) newHour = hour + 12;
                      if (!isPM && hour === 12) newHour = 0;
                      setBookingRules(prev => ({ ...prev, same_day_cutoff_hour: newHour }));
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                        <SelectItem key={h} value={String(h)}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span>:</span>
                  <Select 
                    value={String(bookingRules.same_day_cutoff_minute)}
                    onValueChange={(val) => setBookingRules(prev => ({ ...prev, same_day_cutoff_minute: parseInt(val) }))}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map(m => (
                        <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select 
                    value={bookingRules.same_day_cutoff_hour >= 12 ? 'PM' : 'AM'}
                    onValueChange={(val) => {
                      const currentHour = bookingRules.same_day_cutoff_hour;
                      const isPM = currentHour >= 12;
                      if (val === 'PM' && !isPM) {
                        setBookingRules(prev => ({ ...prev, same_day_cutoff_hour: currentHour + 12 }));
                      } else if (val === 'AM' && isPM) {
                        setBookingRules(prev => ({ ...prev, same_day_cutoff_hour: currentHour - 12 }));
                      }
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Next-Day Bookings */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Allow Next-Day Bookings</Label>
              <p className="text-sm text-gray-500">Let customers book services for tomorrow</p>
            </div>
            <Switch 
              checked={bookingRules.allow_next_day}
              onCheckedChange={(checked) => setBookingRules(prev => ({ ...prev, allow_next_day: checked }))}
            />
          </div>

          {/* Minimum Notice */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Minimum Notice Required</Label>
            <p className="text-sm text-gray-500">For all other bookings, how much advance notice?</p>
            <div className="flex items-center gap-2">
              <Input 
                type="number"
                min="0"
                max="365"
                value={Math.floor(bookingRules.min_notice_hours / 24)}
                onChange={(e) => setBookingRules(prev => ({ ...prev, min_notice_hours: (parseInt(e.target.value) || 0) * 24 }))}
                className="w-24"
              />
              <span className="text-sm text-gray-600">days</span>
            </div>
          </div>

          {/* Current Rules Summary */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Current Settings Summary</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Same-Day: {bookingRules.allow_same_day ? `Yes (before ${formatCutoffTime(bookingRules.same_day_cutoff_hour, bookingRules.same_day_cutoff_minute)} ET)` : 'No'}</li>
              <li>• Next-Day: {bookingRules.allow_next_day ? 'Yes' : 'No'}</li>
              <li>• Min Notice: {Math.floor(bookingRules.min_notice_hours / 24)} days ({bookingRules.min_notice_hours} hours)</li>
            </ul>
          </div>

          {/* Admin Note (Required) */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Reason for Change *</Label>
            <Textarea
              placeholder="Enter the reason for this change (required for audit)"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={2}
            />
          </div>

          {/* Audit History */}
          {auditHistory.length > 0 && (
            <div className="space-y-2">
              <Label className="text-base font-medium">Recent Changes</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg">
                {auditHistory.slice(0, 5).map((log, index) => (
                  <div key={log.id || index} className="p-2 border-b last:border-b-0 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>{log.admin_email}</span>
                      <span>{new Date(log.created_at).toLocaleDateString()}</span>
                    </div>
                    {log.admin_note && <p className="text-gray-500 text-xs mt-1">{log.admin_note}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setBookingRulesOpen(false)}>
            Cancel
          </Button>
          <Button onClick={saveBookingRules} disabled={savingRules || !adminNote.trim()}>
            {savingRules ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Fee Status Dialog */}
    <Dialog open={feeStatusOpen} onOpenChange={setFeeStatusOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            Platform Fee: {selectedVendor?.business_name}
          </DialogTitle>
          <DialogDescription>
            View and manage this vendor&apos;s platform fee settings
          </DialogDescription>
        </DialogHeader>
        
        {feeStatus && (
          <div className="space-y-4 py-4">
            {/* Current Fee Status */}
            <div className={`p-4 rounded-lg ${feeStatus.is_promo ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-700">Current Fee Rate</span>
                <Badge className={feeStatus.is_promo ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}>
                  {feeStatus.effective_fee_percent}%
                </Badge>
              </div>
              
              {feeStatus.is_promo ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-purple-600" />
                    <span className="text-sm text-purple-800 font-medium">Promo Active</span>
                  </div>
                  <p className="text-sm text-purple-700">{feeStatus.promo_reason}</p>
                  <p className="text-xs text-purple-600">
                    Expires: {new Date(feeStatus.promo_end_date).toLocaleDateString()} ({feeStatus.days_remaining} days remaining)
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Using default platform fee ({feeStatus.global_fee_percent}%)</p>
              )}
            </div>

            {/* Admin Actions */}
            <div className="border-t pt-4 space-y-3">
              <p className="font-medium text-gray-900">Admin Actions</p>
              
              {!feeStatus.is_promo ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Apply Waiver for</Label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={waiverDays}
                      onChange={(e) => setWaiverDays(parseInt(e.target.value) || 90)}
                      className="w-20"
                    />
                    <Label className="text-sm">days</Label>
                  </div>
                  <Button
                    onClick={() => applyFeeWaiver(false)}
                    disabled={feeActionLoading}
                    data-testid="apply-fee-waiver-btn"
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <Gift className="h-4 w-4 mr-2" />
                    {feeActionLoading ? 'Applying...' : `Apply ${waiverDays}-Day Fee Waiver (0%)`}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Extend Waiver */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Extend by</Label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={extendDays}
                      onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                      className="w-20"
                    />
                    <Label className="text-sm">days</Label>
                    <Button
                      onClick={extendFeeWaiver}
                      disabled={feeActionLoading}
                      variant="outline"
                      size="sm"
                    >
                      {feeActionLoading ? '...' : 'Extend'}
                    </Button>
                  </div>
                  
                  {/* End Waiver */}
                  <Button
                    onClick={endFeeWaiver}
                    disabled={feeActionLoading}
                    variant="destructive"
                    className="w-full"
                  >
                    {feeActionLoading ? 'Ending...' : 'End Promo Now (Revert to Default Fee)'}
                  </Button>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <p className="font-medium">💡 Note</p>
              <p>Fee changes apply to new bookings only. Existing bookings keep their original fee structure.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setFeeStatusOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Phase 2: Confirm-and-force override of an existing active waiver */}
    <Dialog open={overrideConfirmOpen} onOpenChange={setOverrideConfirmOpen}>
      <DialogContent className="max-w-md" data-testid="override-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-600" />
            Active Waiver Already Exists
          </DialogTitle>
          <DialogDescription>
            {overrideConflict?.message || 'This vendor already has an active waiver.'}
          </DialogDescription>
        </DialogHeader>

        {overrideConflict && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Current rate:</span>
              <span className="font-medium text-gray-900">
                {overrideConflict.currentFeePercent != null ? `${overrideConflict.currentFeePercent}%` : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Active until:</span>
              <span className="font-medium text-gray-900">
                {overrideConflict.activeUntil ? overrideConflict.activeUntil.split('T')[0] : '—'}
              </span>
            </div>
            {overrideConflict.currentReason && (
              <div className="pt-1 border-t border-amber-200">
                <span className="text-gray-600">Reason: </span>
                <span className="text-gray-800">{overrideConflict.currentReason}</span>
              </div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-600">
          Replacing the existing waiver will overwrite its expiration and reason.
          The previous values are preserved in the audit trail.
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { setOverrideConfirmOpen(false); setOverrideConflict(null); }}
            data-testid="override-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={confirmForceOverride}
            disabled={feeActionLoading}
            data-testid="override-confirm-btn"
          >
            {feeActionLoading ? 'Overriding...' : 'Override Anyway'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default AdminListingsTab;
